import { ReactNode, useState } from "react";
import {
  Collapse,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { ReactComponent as IconCalendar } from "assets/svg/20-calendar.svg";
import pxToRem from "theme/base/pxToRem";
import { DateView } from "@mui/x-date-pickers";
import { get } from "lodash";
import { DatePickerField, Label } from "./styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Required } from "../FormInput/style";

interface SelectProps {
  name: string;
  formData: object;
  formErrors?: object;
  label?: string;
  labelIcon?: ReactNode;
  attachRef?: Function;
  onChange?: Function;
  restProps?: object;
  disabled?: boolean;
  placeholder?: string;
  format?: string;
  size?: "small" | "medium";
  maxDate?: moment.Moment | null;
  minDate?: moment.Moment | null;
  views?: DateView[];
  onKeyDown?: (e) => void;
  required?: boolean;
}

const FormDatePicker = ({
  name,
  formData,
  label,
  formErrors,
  labelIcon,
  attachRef,
  onChange,
  maxDate = null,
  minDate = null,
  onKeyDown,
  restProps,
  disabled,
  format = "DD/MM/YYYY",
  views = ["year", "month", "day"],
  size = "small",
  required = false,
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // if (disabled || get(formData, name)) return;
    if (event.key === "Enter") {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      onKeyDown?.(name);
    }, 100);
  };

  const handleOnChange = (e) => {
    onChange?.(name)(e);
  };

  const value = get(formData, name);
  const dateFormats = ["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"]; // Add more formats as needed
  const parsedDate = moment(value, dateFormats, true);

  return (
    <FormControl
      variant="standard"
      fullWidth
      sx={{
        li: {
          padding: pxToRem(16),
        },
      }}
      disabled={disabled}
    >
      {!!label && (
        <Label disabled={disabled} shrink htmlFor={name} variant="standard">
          {labelIcon} {label} {required && <Required>*</Required>}
        </Label>
      )}

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePickerField
          open={isOpen}
          onClose={handleClose}
          slots={{
            textField: TextField,
          }}
          dayOfWeekFormatter={(weekday) => weekday.format("dd")}
          slotProps={{
            textField: {
              id: name,
              inputRef: attachRef?.(name),
              fullWidth: true,
              size: size,
              disabled: disabled,
              onKeyDown: handleKeyDown,
              sx: {
                "& .MuiOutlinedInput-root": {
                  paddingRight: 0,
                  "& fieldset": {
                    border: "none",
                  },
                },
              },
              InputProps: {
                endAdornment: (
                  <IconCalendar
                    style={{ cursor: "pointer", marginRight: 0 }}
                    onClick={() => setIsOpen(true)}
                  />
                ),
              },
            },
          }}
          value={parsedDate}
          onChange={handleOnChange}
          disabled={disabled}
          error={!!formErrors?.[name]}
          format={format}
          maxDate={maxDate}
          minDate={minDate}
          views={views}
          onOpen={() => setIsOpen(true)}
          {...restProps}
        />
      </LocalizationProvider>

      <Collapse in={!!get(formErrors, name)} timeout="auto">
        <FormHelperText error>{get(formErrors, name)}</FormHelperText>
      </Collapse>
    </FormControl>
  );
};

export default FormDatePicker;
